import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Shield } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useLiveDashboard } from "../hooks/useLiveDashboard";
import Card from "../components/Card";
import Button from "../components/Buttons";

const getRiskColor = (level) => {
    switch (level) {
        case "CRITICAL": return "text-red-700 bg-red-100";
        case "HIGH": return "text-orange-600 bg-orange-100";
        case "MEDIUM": return "text-yellow-600 bg-yellow-100";
        case "LOW": return "text-green-600 bg-green-100";
        default: return "text-gray-600 bg-gray-100";
    }
};

const getRiskBarColor = (level) => {
    switch (level) {
        case "CRITICAL": return "bg-red-700";
        case "HIGH": return "bg-orange-500";
        case "MEDIUM": return "bg-yellow-500";
        case "LOW": return "bg-green-500";
        default: return "bg-gray-400";
    }
};

const getTierBadge = (tier) => {
    switch (tier) {
        case 1: return { label: "Tier 1 - Critical", color: "bg-red-100 text-red-800" };
        case 2: return { label: "Tier 2 - Important", color: "bg-yellow-100 text-yellow-800" };
        case 3: return { label: "Tier 3 - Standard", color: "bg-green-100 text-green-800" };
        default: return null;
    }
};

const formatCurrency = (val) => {
    if (val == null || val === 0) return null;
    const n = Number(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString()}`;
};

const CARDS_PER_PAGE = 6;

const VendorCardsSection = ({ onVendorsLoaded }) => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);

    const apiCall = useApi();
    const navigate = useNavigate();

    // `silent` skips the loading toggle so the live background refresh updates the vendor cards — and
    // the TPRM dashboard's vendor-derived headline numbers (via onVendorsLoaded) — in place with no
    // spinner. The mount call passes silent=false for the normal first-load spinner.
    const fetchVendorRiskSummary = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const data = await apiCall("GET", "/TPRMVendor/risk-summary");
            const vendorList = Array.isArray(data) ? data : [];
            setVendors(vendorList);
            if (onVendorsLoaded) onVendorsLoaded(vendorList);
        } catch (err) {
            setVendors([]);
            if (onVendorsLoaded) onVendorsLoaded([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [apiCall, onVendorsLoaded]);

    useEffect(() => { fetchVendorRiskSummary(false); }, [fetchVendorRiskSummary]);

    // Keep the vendor data live so the dashboard's derived scores update on any risk change.
    const liveRefreshVendors = useCallback(() => fetchVendorRiskSummary(true), [fetchVendorRiskSummary]);
    useLiveDashboard({ refresh: liveRefreshVendors });

    const displayedVendors = showAll ? vendors : vendors.slice(0, CARDS_PER_PAGE);

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Vendors</h3>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
            ) : vendors.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No vendor risks found</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {displayedVendors.map((vendor) => (
                            <div
                                key={vendor.vendorId}
                                className="p-4 border border-gray-200 rounded-lg shadow hover:border-green-300 hover:shadow-md transition-all duration-200 cursor-pointer"
                                onClick={() => navigate(`/VendorDashboard/${vendor.vendorId}`)}
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-semibold text-content-primary truncate">
                                        {vendor.vendorName}
                                    </h4>
                                    <div className="flex items-center gap-1.5">
                                        {getTierBadge(vendor.vendorTier) && (
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${getTierBadge(vendor.vendorTier).color}`}>
                                                {getTierBadge(vendor.vendorTier).label}
                                            </span>
                                        )}
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getRiskColor(vendor.riskLevel)}`}>
                                            {vendor.riskLevel}
                                        </span>
                                    </div>
                                </div>

                                {vendor.vendorDomain && (
                                    <p className="text-xs text-gray-400 mb-2 truncate">{vendor.vendorDomain}</p>
                                )}

                                <div className="mt-2">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-gray-600">Risk Score</span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs font-medium">{vendor.riskScore}%</span>
                                            {vendor.forecastScore90Day != null && (
                                                <span className={`text-xs font-semibold ${
                                                    vendor.forecastScore90Day > vendor.riskScore ? "text-red-500" :
                                                    vendor.forecastScore90Day < vendor.riskScore ? "text-green-600" : "text-gray-400"
                                                }`}>
                                                    {vendor.forecastScore90Day > vendor.riskScore ? "↑" :
                                                     vendor.forecastScore90Day < vendor.riskScore ? "↓" : "→"} {vendor.forecastScore90Day}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className={`h-1.5 rounded-full ${getRiskBarColor(vendor.riskLevel)}`}
                                            style={{ width: `${vendor.riskScore}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="mt-3 pt-2 border-t border-gray-100">
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="text-gray-500">Active Risks</span>
                                        <span className="font-bold text-gray-800">{vendor.totalRisks}</span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-1 text-xs">
                                        {vendor.criticalCount > 0 && (
                                            <div className="text-center">
                                                <div className="font-bold text-red-600">{vendor.criticalCount}</div>
                                                <div className="text-gray-500">Critical</div>
                                            </div>
                                        )}
                                        {vendor.highCount > 0 && (
                                            <div className="text-center">
                                                <div className="font-bold text-orange-600">{vendor.highCount}</div>
                                                <div className="text-gray-500">High</div>
                                            </div>
                                        )}
                                        {vendor.mediumCount > 0 && (
                                            <div className="text-center">
                                                <div className="font-bold text-yellow-600">{vendor.mediumCount}</div>
                                                <div className="text-gray-500">Medium</div>
                                            </div>
                                        )}
                                        {vendor.lowCount > 0 && (
                                            <div className="text-center">
                                                <div className="font-bold text-green-600">{vendor.lowCount}</div>
                                                <div className="text-gray-500">Low</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {formatCurrency(vendor.totalExpectedAnnualLoss) && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-gray-500">Expected Annual Loss</span>
                                            <span className="font-bold text-emerald-700">{formatCurrency(vendor.totalExpectedAnnualLoss)}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="mt-3 flex justify-end">
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        className="px-2 py-1 h-8 text-xs flex items-center"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/VendorDashboard/${vendor.vendorId}`);
                                        }}
                                    >
                                        View Dashboard
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {vendors.length > CARDS_PER_PAGE && (
                        <button
                            onClick={() => setShowAll(prev => !prev)}
                            className="mt-4 w-full text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1 py-2 rounded hover:bg-indigo-50 transition-colors"
                        >
                            {showAll
                                ? <><span>&#9650;</span> Show less</>
                                : <><span>&#9660;</span> Show {vendors.length - CARDS_PER_PAGE} more vendors</>
                            }
                        </button>
                    )}
                </>
            )}
        </Card>
    );
};

export default VendorCardsSection;
